import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { BaseCpFormModel, ControlProps, PropagateOpts } from "../../../../models/form/base-cp-form.model";
import { JobTemplateModel } from "../../../../models/job_template/job-template.model";
import { AssigneeEntityType } from "../../../../enums/frontend/AssigneeEntityType";
import Constants from "../../../../Constants";
import { SelectItem } from "primeng/api";
import { Subject, Subscription } from "rxjs";
import { JobStatus } from "../../../../enums/job/JobStatus";
import TargetEntityType from "../../../../enums/frontend/TargetEntityType";
import { JobUtils } from "../../../../utils/JobUtils";
import { Organization } from "../../../../models/organization/Organization";
import AccessMode from '../../../../enums/organization/AccessMode';
import { JOB_ROUTINE_SIMPLE_TYPES } from '../../../../enums/job_template/job-routine-type.enum';

export class JobRoutineBasicInfoFormModel extends BaseCpFormModel<JobRoutineBasicInfoFormModel, JobTemplateModel> {

  //region FIELDS
  //region static DEFAULTS
  static readonly DEFAULT_ASSIGNEE_TYPE: AssigneeEntityType = AssigneeEntityType.USER;
  static readonly DEFAULT_JOB_BEGIN_STATUS: JobStatus = JobStatus.DATE_PROPOSAL;
  //endregion static DEFAULTS

  static readonly assigneeTypes: SelectItem[] = Constants.DROPDOWNS.types.assigneeType;
  static readonly jobStatus: SelectItem[] = Constants.DROPDOWNS.status.jobStatusDesignB_bg;

  //region static FIELDS
  static readonly ID = 'id';
  static readonly TYPE = 'type';
  static readonly TARGET_TYPE = 'targetType';
  static readonly TARGET = 'target';
  static readonly PARENT_TEMPLATE = 'jobTemplate';
  static readonly ACTIVE = 'isActive';
  static readonly IS_PINNED = 'isPinned';
  static readonly NAME = 'title';
  static readonly FLAT_RATE = 'flatRate';
  static readonly PRODUCER_NUMBER = 'producerNumber';
  static readonly CATEGORY = 'category';
  static readonly DESCRIPTION = 'description';
  static readonly EXECUTION_LOCATION_EXTERN = 'executionLocationExtern';
  static readonly RESPONSIBILITY_EXTERN = 'responsibilityExtern';
  static readonly ORGANIZATION = 'organization';
  static readonly SERVICE_PROVIDER = 'serviceProvider';
  static readonly ASSIGNEE_TYPE = 'assigneeType';
  static readonly ASSIGNED_USER = 'assignedUser';
  static readonly ASSIGNED_WORK_GROUP = 'assignedWorkGroup';
  static readonly JOB_BEGIN_STATUS = 'jobBeginStatus';
  static readonly EXECUTION_SERVICE_PROVIDER = 'executionPlaceServiceProvider';

  static readonly START_DATUM_FROM_ATTRIBUTE = 'startDatumFromAttribute';
  static readonly START_DATUM_ATTRIBUTE = 'startDatumAttribute';
  static readonly START_DATUM_VALUE = 'startDatumValue';

  //endregion static FIELDS

  //region controls
  idControl: UntypedFormControl;
  typeControl: UntypedFormControl;
  targetTypeControl: UntypedFormControl;
  targetControl: UntypedFormControl;
  jobTemplateControl: UntypedFormControl;
  isActiveControl: UntypedFormControl;
  isPinnedControl: UntypedFormControl;
  titleControl: UntypedFormControl;
  flatRateControl: UntypedFormControl;
  producerNumberControl: UntypedFormControl;
  categoryControl: UntypedFormControl;
  descriptionControl: UntypedFormControl;
  executionLocationExternControl: UntypedFormControl;
  responsibilityExternControl: UntypedFormControl;
  organizationControl: UntypedFormControl;
  serviceProviderControl: UntypedFormControl;
  assigneeTypeControl: UntypedFormControl;
  assignedUserControl: UntypedFormControl;
  assignedWorkGroupControl: UntypedFormControl;
  jobBeginStatusControl: UntypedFormControl;
  executionPlaceServiceProviderControl: UntypedFormControl;

  startDatumFromAttributeControl: UntypedFormControl;
  startDatumValueControl: UntypedFormControl;
  startDatumAttributeControl: UntypedFormControl;
  //endregion controls

  form: UntypedFormGroup;

  //region defaults
  defaultAssigneeType: SelectItem = JobRoutineBasicInfoFormModel.getDefaultAssigneeType();
  defaultJobBeginStatus: SelectItem = JobRoutineBasicInfoFormModel.getDefaultJobBeginStatus();
  //endregion defaults

  //endregion FIELDS

  asFormGroup(): UntypedFormGroup {
    this.form.addControl(JobRoutineBasicInfoFormModel.ID, this.idControl);
    this.form.addControl(JobRoutineBasicInfoFormModel.TYPE, this.typeControl);
    this.form.addControl(JobRoutineBasicInfoFormModel.TARGET_TYPE, this.targetTypeControl);
    this.form.addControl(JobRoutineBasicInfoFormModel.TARGET, this.targetControl);
    this.form.addControl(JobRoutineBasicInfoFormModel.PARENT_TEMPLATE, this.jobTemplateControl);
    this.form.addControl(JobRoutineBasicInfoFormModel.ACTIVE, this.isActiveControl);
    this.form.addControl(JobRoutineBasicInfoFormModel.IS_PINNED, this.isPinnedControl);
    this.form.addControl(JobRoutineBasicInfoFormModel.NAME, this.titleControl);
    this.form.addControl(JobRoutineBasicInfoFormModel.FLAT_RATE, this.flatRateControl);
    this.form.addControl(JobRoutineBasicInfoFormModel.PRODUCER_NUMBER, this.producerNumberControl);
    this.form.addControl(JobRoutineBasicInfoFormModel.CATEGORY, this.categoryControl);
    this.form.addControl(JobRoutineBasicInfoFormModel.DESCRIPTION, this.descriptionControl);
    this.form.addControl(JobRoutineBasicInfoFormModel.EXECUTION_LOCATION_EXTERN, this.executionLocationExternControl);
    this.form.addControl(JobRoutineBasicInfoFormModel.RESPONSIBILITY_EXTERN, this.responsibilityExternControl);
    this.form.addControl(JobRoutineBasicInfoFormModel.ORGANIZATION, this.organizationControl);
    this.form.addControl(JobRoutineBasicInfoFormModel.SERVICE_PROVIDER, this.serviceProviderControl);
    this.form.addControl(JobRoutineBasicInfoFormModel.ASSIGNEE_TYPE, this.assigneeTypeControl);
    this.form.addControl(JobRoutineBasicInfoFormModel.ASSIGNED_USER, this.assignedUserControl);
    this.form.addControl(JobRoutineBasicInfoFormModel.ASSIGNED_WORK_GROUP, this.assignedWorkGroupControl);
    this.form.addControl(JobRoutineBasicInfoFormModel.JOB_BEGIN_STATUS, this.jobBeginStatusControl);
    this.form.addControl(JobRoutineBasicInfoFormModel.EXECUTION_SERVICE_PROVIDER, this.executionPlaceServiceProviderControl);
    this.form.addControl(JobRoutineBasicInfoFormModel.START_DATUM_FROM_ATTRIBUTE, this.startDatumFromAttributeControl);
    this.form.addControl(JobRoutineBasicInfoFormModel.START_DATUM_VALUE, this.startDatumValueControl);
    this.form.addControl(JobRoutineBasicInfoFormModel.START_DATUM_ATTRIBUTE, this.startDatumAttributeControl);
    return this.form;
  }

  asModel(formGroup: UntypedFormGroup, model: JobTemplateModel): JobTemplateModel {
    model[JobRoutineBasicInfoFormModel.ID] = formGroup.get(JobRoutineBasicInfoFormModel.ID).value;
    model[JobRoutineBasicInfoFormModel.TYPE] = formGroup.get(JobRoutineBasicInfoFormModel.TYPE).value;
    model[JobRoutineBasicInfoFormModel.TARGET_TYPE] = formGroup.get(JobRoutineBasicInfoFormModel.TARGET_TYPE).value;
    model[JobRoutineBasicInfoFormModel.TARGET] = formGroup.get(JobRoutineBasicInfoFormModel.TARGET).value;
    model[JobRoutineBasicInfoFormModel.PARENT_TEMPLATE] = formGroup.get(JobRoutineBasicInfoFormModel.PARENT_TEMPLATE).value;
    model[JobRoutineBasicInfoFormModel.ACTIVE] = formGroup.get(JobRoutineBasicInfoFormModel.ACTIVE).value;
    model[JobRoutineBasicInfoFormModel.IS_PINNED] = formGroup.get(JobRoutineBasicInfoFormModel.IS_PINNED).value;
    model[JobRoutineBasicInfoFormModel.NAME] = formGroup.get(JobRoutineBasicInfoFormModel.NAME).value;
    model[JobRoutineBasicInfoFormModel.FLAT_RATE] = formGroup.get(JobRoutineBasicInfoFormModel.FLAT_RATE).value;
    model[JobRoutineBasicInfoFormModel.PRODUCER_NUMBER] = formGroup.get(JobRoutineBasicInfoFormModel.PRODUCER_NUMBER).value;
    model[JobRoutineBasicInfoFormModel.CATEGORY] = formGroup.get(JobRoutineBasicInfoFormModel.CATEGORY).value;
    model[JobRoutineBasicInfoFormModel.DESCRIPTION] = formGroup.get(JobRoutineBasicInfoFormModel.DESCRIPTION).value;
    model[JobRoutineBasicInfoFormModel.EXECUTION_LOCATION_EXTERN] = formGroup.get(JobRoutineBasicInfoFormModel.EXECUTION_LOCATION_EXTERN).value;
    model[JobRoutineBasicInfoFormModel.RESPONSIBILITY_EXTERN] = formGroup.get(JobRoutineBasicInfoFormModel.RESPONSIBILITY_EXTERN).value;
    model[JobRoutineBasicInfoFormModel.ORGANIZATION] = formGroup.get(JobRoutineBasicInfoFormModel.ORGANIZATION).value;
    model[JobRoutineBasicInfoFormModel.SERVICE_PROVIDER] = formGroup.get(JobRoutineBasicInfoFormModel.SERVICE_PROVIDER).value;
    model[JobRoutineBasicInfoFormModel.ASSIGNEE_TYPE] = formGroup.get(JobRoutineBasicInfoFormModel.ASSIGNEE_TYPE).value;
    model[JobRoutineBasicInfoFormModel.ASSIGNED_USER] = formGroup.get(JobRoutineBasicInfoFormModel.ASSIGNED_USER).value;
    model[JobRoutineBasicInfoFormModel.ASSIGNED_WORK_GROUP] = formGroup.get(JobRoutineBasicInfoFormModel.ASSIGNED_WORK_GROUP).value;
    model[JobRoutineBasicInfoFormModel.JOB_BEGIN_STATUS] = formGroup.get(JobRoutineBasicInfoFormModel.JOB_BEGIN_STATUS).value;
    model[JobRoutineBasicInfoFormModel.EXECUTION_SERVICE_PROVIDER] = formGroup.get(JobRoutineBasicInfoFormModel.EXECUTION_SERVICE_PROVIDER).value;
    model[JobRoutineBasicInfoFormModel.START_DATUM_FROM_ATTRIBUTE] = formGroup.get(JobRoutineBasicInfoFormModel.START_DATUM_FROM_ATTRIBUTE).value;
    model[JobRoutineBasicInfoFormModel.START_DATUM_VALUE] = formGroup.get(JobRoutineBasicInfoFormModel.START_DATUM_VALUE).value;
    model[JobRoutineBasicInfoFormModel.START_DATUM_ATTRIBUTE] = formGroup.get(JobRoutineBasicInfoFormModel.START_DATUM_ATTRIBUTE).value;

    return model;
  }

  createForm(
    formModel: JobRoutineBasicInfoFormModel,
    model: JobTemplateModel,
    accessMode?: AccessMode
  ): JobRoutineBasicInfoFormModel {
    const idProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.ID);
    this.initControl(formModel, model, idProps);

    const typeProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.TYPE);
    const typeValue = model?.type ?
      Constants.DROPDOWNS.jobRoutineType.find(e => e.value === model.type) :
      Constants.DROPDOWNS.jobRoutineType[0];
    typeProps.withValue(typeValue);
    this.initControl(formModel, model, typeProps);

    const isObjectRoutine: boolean = !!model?.componentId;
    const isModuleRoutineTemplate: boolean = !!model?.moduleId;
    let defaultTargetType = null;
    let defaultTarget = null;
    if (isObjectRoutine) {
      defaultTargetType = {label: '', value: TargetEntityType.OBJECT};
      defaultTarget = {};
    } else if (isModuleRoutineTemplate) {
      defaultTargetType = {name: '', value: TargetEntityType.MODULE};
    }
    const targetTypeProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.TARGET_TYPE);
    targetTypeProps.required();
    targetTypeProps.withValue(defaultTargetType);
    this.initControl(formModel, model, targetTypeProps);

    const target = defaultTargetType ? (isObjectRoutine ? {id: model?.componentId, label: model.component?.serialNumber} : {id: model.moduleId, label: model.moduleName}) : null;
    const targetProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.TARGET);
    targetProps.required();
    targetProps.withValue(target);
    this.initControl(formModel, model, targetProps);

    const jobTemplateProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.PARENT_TEMPLATE);
    jobTemplateProps.withValue(model?.parentId ? {id: model.parentId, name: model.parentName} : null);
    this.initControl(formModel, model, jobTemplateProps);

    const activeProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.ACTIVE);
    activeProps.required();
    activeProps.withDefaultValue(true);
    activeProps.withValue(true);
    this.initControl(formModel, model, activeProps);

    const isPinnedProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.IS_PINNED);
    isPinnedProps.withDefaultValue(false);
    isPinnedProps.withValue(model?.isPinned ? model.isPinned : false);
    this.initControl(formModel, model, isPinnedProps);

    const nameProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.NAME);
    nameProps.required();
    nameProps.withValue(model?.name ? model.name : null);
    this.initControl(formModel, model, nameProps);

    const flatRateProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.FLAT_RATE);
    flatRateProps.withValue(model?.flatRate ? model.flatRate : null);
    this.initControl(formModel, model, flatRateProps);

    const producerNumberProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.PRODUCER_NUMBER);
    producerNumberProps.withValue(model?.producerNumber ? model.producerNumber : null);
    this.initControl(formModel, model, producerNumberProps);

    const categoryProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.CATEGORY);
    categoryProps.required();
    categoryProps.withValue(model?.jobType ? model.jobType : null);
    this.initControl(formModel, model, categoryProps);

    const descriptionProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.DESCRIPTION);
    descriptionProps.withValue(model?.description ? model.description : null);
    this.initControl(formModel, model, descriptionProps);

    const executionLocationExternProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.EXECUTION_LOCATION_EXTERN);
    executionLocationExternProps.withDefaultValue(false);
    const val = model ? !(model.inHouseExecution === undefined || model.inHouseExecution) : false;
    executionLocationExternProps.withValue(val);
    this.initControl(formModel, model, executionLocationExternProps);

    const responsibilityExternProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.RESPONSIBILITY_EXTERN);
    responsibilityExternProps.required();
    let responsibilityExternValue: boolean;
    if (accessMode === AccessMode.SERVICE_PROVIDER) {
      responsibilityExternValue = model?.serviceProvider === undefined ? true : !!model?.serviceProvider;
      responsibilityExternProps.withDefaultValue(true);
      responsibilityExternProps.withValue(responsibilityExternValue);
      responsibilityExternProps.disable();
    } else {
      responsibilityExternValue = !!model?.serviceProvider;
      responsibilityExternProps.withDefaultValue(false);
      responsibilityExternProps.withValue(responsibilityExternValue);
    }
    this.initControl(formModel, model, responsibilityExternProps);

    const organizationProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.ORGANIZATION);
    if (responsibilityExternValue === false) {
      organizationProps.required();
    }
    organizationProps.withValue(model?.ownerOrg || null);
    this.initControl(formModel, model, organizationProps);

    const serviceProviderProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.SERVICE_PROVIDER);
    if (responsibilityExternValue === true) {
      serviceProviderProps.required();
    }
    serviceProviderProps.withValue(model?.serviceProvider || null);
    this.initControl(formModel, model, serviceProviderProps);

    const executionServiceProviderProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.EXECUTION_SERVICE_PROVIDER);
    executionServiceProviderProps.withValue(model?.executionPlaceServiceProvider || null);
    this.initControl(formModel, model, executionServiceProviderProps);

    const assigneeTypeProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.ASSIGNEE_TYPE);
    assigneeTypeProps.required();
    let assigneeType = this.defaultAssigneeType;
    if (model?.assigneeUser) {
      assigneeType = [...JobRoutineBasicInfoFormModel.assigneeTypes].find(a => a.value == AssigneeEntityType.USER);
    } else if (model?.assigneeGroup) {
      assigneeType = [...JobRoutineBasicInfoFormModel.assigneeTypes].find(a => a.value == AssigneeEntityType.WORKGROUP);
    }
    if (assigneeType) {
      assigneeTypeProps.withValue(assigneeType);
    } else {
      assigneeTypeProps.withDefaultValue(this.defaultAssigneeType);
    }
    this.initControl(formModel, model, assigneeTypeProps);

    const assignedUserProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.ASSIGNED_USER);
    if (isObjectRoutine && !model?.serviceProvider && assigneeType?.value == AssigneeEntityType.USER) {
      assignedUserProps.required();
    }
    assignedUserProps.withValue(model?.assigneeUser ? model.assigneeUser : null);
    this.initControl(formModel, model, assignedUserProps);

    const assignedWorkGroupProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.ASSIGNED_WORK_GROUP);
    if (isObjectRoutine && !model?.serviceProvider && assigneeType?.value == AssigneeEntityType.WORKGROUP) {
      assignedWorkGroupProps.required();
    }
    assignedWorkGroupProps.withValue(model?.assigneeGroup ? model.assigneeGroup : null);
    this.initControl(formModel, model, assignedWorkGroupProps);

    const jobBeginValue = model?.jobBeginStatus ? JobRoutineBasicInfoFormModel.jobStatus.find(e => e.value === JobUtils.getStatusOfStatusInt(model.jobBeginStatus)) : null;
    const jobBeginStatusProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.JOB_BEGIN_STATUS);
    jobBeginStatusProps.withDefaultValue(this.defaultJobBeginStatus);
    jobBeginStatusProps.withValue(jobBeginValue);
    this.initControl(formModel, model, jobBeginStatusProps);

    const startDateFromAttributeProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.START_DATUM_FROM_ATTRIBUTE);
    startDateFromAttributeProps.withValue(false);
    this.initControl(formModel, model, startDateFromAttributeProps);

    const startDateValueProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.START_DATUM_VALUE);
    startDateValueProps.withValue(new Date());
    this.initControl(formModel, model, startDateValueProps);

    const startDateAttributeProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.START_DATUM_ATTRIBUTE);
    startDateAttributeProps.withValue(null);
    this.initControl(formModel, model, startDateAttributeProps);

    formModel.form = new UntypedFormGroup({});

    return formModel;
  }

  //region setup valueChanges
  setupTypeValueChanges(): Subscription {
    return this.typeControl.valueChanges.subscribe(v => {
      if (JOB_ROUTINE_SIMPLE_TYPES.includes(v?.value)) {
        const userAssigneeType = [...JobRoutineBasicInfoFormModel.assigneeTypes]
          .find(a => a.value == AssigneeEntityType.USER);

        this.assigneeTypeControl.setValue(userAssigneeType);
        this.assignedWorkGroupControl.setValue(null);

        this.assigneeTypeControl.clearValidators();
        this.assignedUserControl.clearValidators();
        this.assignedWorkGroupControl.clearValidators();
      } else {
        this.assigneeTypeControl.setValidators(Validators.required);

        if (this.assigneeTypeControl.value?.value == AssigneeEntityType.WORKGROUP) {
          if (!this.responsibilityExternControl.value) {
            this.assignedWorkGroupControl.setValidators(Validators.required);
          }
        } else if (!this.responsibilityExternControl.value) {
          this.assignedUserControl.setValidators(Validators.required);
        }
      }

      // NOTE: updateValueAndValidity() on assigneeTypeControl would re-emit its own
      // valueChanges (even without a value change), which re-triggers
      // setupAssigneeTypeValueChanges() and stomps on the validators we just set above.
      // Recompute its own status/errors quietly (no emit, no parent propagation) and let
      // the assignedUser/assignedWorkGroup updates below propagate the change upward.
      this.assigneeTypeControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      this.assignedUserControl.updateValueAndValidity();
      this.assignedWorkGroupControl.updateValueAndValidity();
    });
  }

  setupAssigneeTypeValueChanges(): Subscription {
    return this.assigneeTypeControl.valueChanges.subscribe(v => {
      switch (v.value) {
        case AssigneeEntityType.USER: {
          this.assignedWorkGroupControl.setValue(null);
          if (!this.responsibilityExternControl.value) {
            this.assignedUserControl.setValidators(Validators.required);
          }
          this.assignedWorkGroupControl.clearValidators();
          break;
        }

        case AssigneeEntityType.WORKGROUP: {
          this.assignedUserControl.setValue(null);
          if (!this.responsibilityExternControl.value) {
            this.assignedWorkGroupControl.setValidators(Validators.required);
          }
          this.assignedUserControl.clearValidators();
          break;
        }
      }

      this.assignedUserControl.updateValueAndValidity();
      this.assignedWorkGroupControl.updateValueAndValidity();
    });
  }

  setupExecutionLocationValueChanges(): Subscription {
    return this.executionLocationExternControl.valueChanges.subscribe(v => {
      if (!v) {
        this.executionPlaceServiceProviderControl.setValue(null);
        this.executionPlaceServiceProviderControl.updateValueAndValidity();
      } else if (this.responsibilityExternControl?.value) {
        this.executionPlaceServiceProviderControl.setValue(
          this.serviceProviderControl?.getRawValue(),
          PropagateOpts.EMIT_EVENT_FALSE
        );
        this.executionPlaceServiceProviderControl.updateValueAndValidity();
      }
    });
  }

  setupResponsibilityInternValueChanges(organizationId$?: Subject<string>, defaultOrg?: Organization): Subscription {
    return this.responsibilityExternControl.valueChanges.subscribe(v => {
      if (organizationId$) {
        organizationId$.next(defaultOrg ? defaultOrg.id : null);
      }
      this.assigneeTypeControl.setValue(this.defaultAssigneeType);
      this.assignedUserControl.setValue(null);
      this.assignedWorkGroupControl.setValue(null);

      this.assigneeTypeControl.updateValueAndValidity();
      this.assignedWorkGroupControl.updateValueAndValidity();

      if (!v) {
        this.serviceProviderControl.setValue(null);
        this.serviceProviderControl.clearValidators();
        this.organizationControl.setValidators(Validators.required);
        this.assignedUserControl.setValidators(Validators.required);
      } else {
        this.serviceProviderControl.setValidators(Validators.required);
        this.organizationControl.setValue(defaultOrg);
        this.organizationControl.clearValidators();
        this.assignedUserControl.clearValidators();
        this.assignedWorkGroupControl.clearValidators();
      }
      this.serviceProviderControl.updateValueAndValidity();
      this.organizationControl.updateValueAndValidity();

      this.assignedUserControl.updateValueAndValidity();
      this.assignedWorkGroupControl.updateValueAndValidity();
    });
  }

  setupServiceProviderValueChanges(organizationId$: Subject<string>, serviceProviderId$?: Subject<string>): Subscription {
    return this.serviceProviderControl.valueChanges.subscribe(v => {
      if (v) {
        organizationId$.next(v.authorOrgId);
        serviceProviderId$.next(v.id);
        this.executionPlaceServiceProviderControl.setValue(this.serviceProviderControl?.value);
        this.executionPlaceServiceProviderControl.updateValueAndValidity();
      } else {
        organizationId$.next(null);
        serviceProviderId$.next(null);
        this.executionPlaceServiceProviderControl.setValue(null);
        this.executionPlaceServiceProviderControl.updateValueAndValidity();
      }

      this.assignedUserControl.reset();
      this.assignedWorkGroupControl.reset();
    });
  }

  setupOrganizationValueChanges(organizationId$?: Subject<string>): Subscription {
    return this.organizationControl.valueChanges.subscribe(v => {
      this.assignedUserControl.reset();
      this.assignedWorkGroupControl.reset();

      if (organizationId$) {
        organizationId$.next(v?.id || null);
      }
    });
  }

  //endregion setup valueChanges

  //region control setters
  public setTargetType(withValue: SelectItem, disabled: boolean = false): void {
    const targetType: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.TARGET_TYPE);
    targetType.withValue(withValue);
    if (disabled) {
      targetType.disable();
    }
    this.setControlValue(targetType);
  }

  public setTarget(withValue: any, disabled: boolean = false): void {
    const targetProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.TARGET);
    targetProps.withValue(withValue);
    if (disabled) {
      targetProps.disable();
    }
    this.setControlValue(targetProps);
  }

  public setOrganization(withValue: { id: string; name: string }): void {
    const organizationProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.ORGANIZATION);
    organizationProps.withValue(withValue);
    this.setControlValue(organizationProps);
  }

  public setCategory(withValue: SelectItem, disabled: boolean = false): void {
    const categoryProps: ControlProps = ControlProps.of(JobRoutineBasicInfoFormModel.CATEGORY);
    categoryProps.withValue(withValue);
    if (disabled) {
      categoryProps.disable();
    }
    this.setControlValue(categoryProps);
  }
  //endregion control setters

  setDefaultAssigneeType(enumEntry: AssigneeEntityType): void {
    this.defaultAssigneeType = JobRoutineBasicInfoFormModel.assigneeTypes.find(e => e.value === enumEntry);
  }

  setDefaultJobBeginStatus(enumEntry: JobStatus): void {
    this.defaultJobBeginStatus = JobRoutineBasicInfoFormModel.jobStatus.find(e => e.value === enumEntry);
  }

  static getDefaultAssigneeType(): SelectItem {
    return {... this.assigneeTypes.find(e => e.value === JobRoutineBasicInfoFormModel.DEFAULT_ASSIGNEE_TYPE)};
  }

  static getDefaultJobBeginStatus(): SelectItem {
    return this.jobStatus.find(e => e.value === JobRoutineBasicInfoFormModel.DEFAULT_JOB_BEGIN_STATUS);
  }

  static getAssigneeTypeItem(assigneeType: AssigneeEntityType): SelectItem {
    return this.assigneeTypes.find(e => e.value === assigneeType);
  }

}
